<template>
    <div>
        <!--Create or Update Bookmark-->
        <v-dialog
            persistent
            v-model="modal"
            max-width="700">
            <v-card>
                <v-card-title
                    class="primary white--text"
                    :class="{
                        'headline': !isMobilePort,
                        'caption mobile-title': isMobilePort
                    }">
                    <span v-if="isUpdate">Edit Note / Bookmark</span>
                    <span v-else>Add Note / Bookmark</span>
                    <v-spacer></v-spacer>
                    <v-icon class="primary white--text" @click="closeModal">close</v-icon>
                </v-card-title>
                <v-card-text class="inner-modal-content">
                    <v-row>
                        <v-col class="font-weight-bold pb-0">
                            Note (optional)
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <vue-editor v-model="editorData" :editor-toolbar="customToolbar" />
                        </v-col>
                    </v-row>
                    <v-row align="center">
                        <template v-if="!loadingTags">
                            <v-col>
                                <v-autocomplete
                                    v-model="selectedTag"
                                    :items="tags"
                                    item-text="tag"
                                    item-value="id"
                                    chips
                                    small-chips
                                    outlined
                                    dense
                                    label="Project Tag"
                                    :rules="[v => !!v || 'Project tag is required']"
                                    hide-details="auto"
                                    ref="projectTag">
                                </v-autocomplete>
                            </v-col>
                            <v-col cols="auto">
                                <v-btn
                                    :disabled="selectedTag === 1"
                                    outlined
                                    @click="createNewTagModal(true, selectedTag)"
                                >
                                    Edit Tag
                                </v-btn>
                                <v-btn
                                    outlined
                                    @click="createNewTagModal(false, selectedTag)"
                                >
                                    Add Tag
                                </v-btn>
                            </v-col>
                        </template>
                        <template v-else>
                            <v-col>
                                <v-progress-linear :indeterminate="true"></v-progress-linear>
                            </v-col>
                        </template>
                    </v-row>

                    <v-row v-if="activeEnterpriseProfile">
                        <v-col>
                            <v-select
                                v-model="selectedVisibility"
                                :items="visibilityItems"
                                label="Visibility"
                                outlined
                                flat
                                hide-details
                                dense
                            >
                                <template v-slot:selection="{ item }">
                                    <v-icon class="mr-2">{{ item.icon }}</v-icon>
                                    {{ item.text }}
                                </template>

                                <template v-slot:item="{ item }">
                                    <v-icon class="mr-2">{{ item.icon }}</v-icon>
                                    {{ item.text }}
                                </template>
                            </v-select>
                        </v-col>
                    </v-row>

                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeModal">Cancel</v-btn>
                    <v-btn color="primary" :loading="isSaving" @click="saveBookmark">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!--Remove Highlight annotation or bookmark Modal-->
        <v-dialog v-model="removeModal" max-width="400">
            <v-card>
                <v-card-title
                    class="primary white--text"
                    :class="{
                        'headline': !isMobilePort,
                        'caption mobile-title': isMobilePort
                    }">
                    Delete Note / Bookmark
                    <v-spacer></v-spacer>
                    <v-icon class="primary white--text" @click="removeModal = false">close</v-icon>
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col class="text-center">
                            <p v-if="isEnterpriseAdminAction()" class="mb-0 mt-5">
                                Are you sure you want to delete this public bookmark from {{getBookmarkCreatorName()}}? It will be removed for everyone in your Enterprise.
                            </p>
                            <p v-else class="mb-0 mt-5">
                                Are you sure you want to remove this bookmark?
                            </p>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="removeModal = false">Cancel</v-btn>
                    <v-btn color="error" :loading="isDeleting" @click="deleteBookmark">Delete</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!--Validate URLs in the user note -->
        <v-dialog v-model="invalidNoteUrl" max-width="400">
            <v-card>
                <v-card-title class="headline">Invalid URL</v-card-title>
                <v-card-text>
                    Your note contains an Invalid URL?
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" flat @click="invalidNoteUrl = false">Ok</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import TagsMixins from './mixins/tags';
    import urlHelper from './mixins/urlHelper';
    import Mobile from '../../inc/mixins/mobile';
    import EditorConfigs from '../../inc/editorConfigBar';
    import { EventNames } from '../../../classes/EventNames';

    export default {
        data() {
            return {
                modal: false,
                removeModal: false,
                bookmarkId: 0,
                bookmark: {},
                editorData: '',
                isUpdate: false,
                invalidNoteUrl: false,
                sectionId: '',
                isSaving: false,
                isDeleting: false,
                // Visibility
                selectedVisibility: 'private',
                visibilityItems: [
                    {value: 'private', text: 'Private (visible to just you)', icon: 'mdi-eye-off-outline'},
                    {value: 'public', text: 'Public (visible to everyone in your Enterprise)', icon: 'mdi-eye-outline'}
                ]
            }
        },
        mixins: [TagsMixins, urlHelper, Mobile, EditorConfigs],
        computed: {
            currentUserId() {
                return parseInt(this.$store?.getters?.getUserId);
            }
        },
        methods: {
            saveBookmark() {
                // Validate URLs in the user note
                if(!this.validateLinksInNote(this.editorData)) {
                    this.invalidNoteUrl = true;
                    return false;
                }

                const isValid = this.$refs.projectTag.validate();
                if (!isValid) {
                    return;
                }

                let bookmarkObj = {
                    tag: this.selectedTag,
                    note: this.editorData,
                    data: this.bookmark,
                    visibility: this.selectedVisibility,
                };
                this.isSaving = true;
                this.$http.post(Routing.generate('save_bookmark'), bookmarkObj)
                    .then(response => {
                        let options;
                        if (this.isUpdate) {
                            options = {
                                action: 'update',
                                sectionId: response.data[0].section_id,
                                type: 'bookmark',
                                id: response.data[0].id,
                                notes: {
                                    bookmarks: response.data
                                }
                            };
                            response.data[0].isUpdate = true;
                            EventBus.fire('update-note-preview-'+response.data[0].id, response.data[0]);
                        } else {
                            options = {
                                action: 'create',
                                sectionId: response.data[0].section_id,
                                notes: {
                                    bookmarks: response.data
                                }
                            };
                        }
                        // Set the bookmark visibility
                        if(response?.data[0]?.is_public) {
                            this.selectedVisibility = 'public';
                        } else {
                            this.selectedVisibility = 'private';
                        }
                        EventBus.fire('update-notes', options);
                        this.closeModal();
                        //if enterprise admin making the bookmark private then hide it immediately from the list
                        if (this.hideableBookmark(response.data[0]) && this.selectedVisibility === 'private') {
                            this.hidePrivateBookmark(response.data[0].id, response.data[0].section_id);
                        }

                    })
                    .finally(()=>{
                        this.isSaving = false;
                    })
            },

            updateBookmark(bookmark) {
                this.isUpdate = true;
                this.$http.get(Routing.generate('get_bookmark', {id: bookmark.id}))
                    .then(response => {
                        let data = response.data;
                        this.bookmark = response.data;
                        this.getUserTags(data.tag);
                        this.editorData = data.text;

                        // Set the bookmark visibility
                        if(data?.is_public) {
                            this.selectedVisibility = 'public';
                        } else {
                            this.selectedVisibility = 'private';
                        }
                    });
            },
            closeModal(){
                this.bookmark = {};
                this.editorData = '';
                this.tags = [];
                this.selectedTag = [];
                this.isUpdate = false;
                this.loadingTags = true;
                this.modal = false;
            },

            deleteBookmark() {
                this.isDeleting = true;
                this.$http.get(Routing.generate('remove_bookmark', {id: this.bookmarkId}))
                    .then(response => {
                        let options = {
                            action: 'remove',
                            type: 'bookmark',
                            id: this.bookmarkId,
                            sectionId: this.sectionId
                        };
                        EventBus.fire('update-notes', options);
                        this.removeModal = false;
                    })
                    .finally(()=>{
                        this.isDeleting = false;
                    })
            },
            isEnterpriseBookmark() {
                return Boolean(this.bookmark?.enterprise);
            },
            getBookmarkCreatorName() {
                return this.bookmark?.userName;
            },
            getBookmarkCreatorId() {
                if(this.bookmark.user && this.bookmark.user.id) {
                    return parseInt(this.bookmark.user.id);
                } else if(undefined !== this.bookmark.user) {
                    return parseInt(this.bookmark.user);
                }
                return 0;
            },
            isEnterpriseAdminAction() {
                return this.isEnterpriseBookmark() &&
                    this.currentUserId !== this.getBookmarkCreatorId();
            },
            onBookmarkModal(bookmark) {
                if (bookmark.id) {
                    this.updateBookmark(bookmark);
                } else {
                    this.bookmark = bookmark;
                    this.selectedVisibility = this.activeEnterpriseProfile ? 'public' : 'private';
                    this.getUserTags(1);
                }
                this.modal = true;
            },
            onRemoveBookmark(data) {
                this.bookmarkId = data.id;
                this.sectionId = data.sectionId;
                this.removeModal = true;
            },
            hidePrivateBookmark(bookmarkId, sectionId) {
                let options = {
                    action: 'remove',
                    type: 'bookmark',
                    id: bookmarkId,
                    sectionId: sectionId
                };
                EventBus.fire('update-notes', options);
            },
            hideableBookmark(bookmarkObj) {
                return bookmarkObj?.enterprise && this.isEnterpriseAdmin
                    && this.currentUserId !== parseInt(bookmarkObj?.user?.id);
            }
        },
        created() {
            EventBus.listen(EventNames.BOOKMARK_MODAL, this.onBookmarkModal);
            EventBus.listen(EventNames.REMOVE_BOOKMARK, this.onRemoveBookmark);
            EventBus.listen(EventNames.UPDATE_NOTE_TAGS, this.updateUserTags);
        },
        beforeDestroy() {
            EventBus.off(EventNames.BOOKMARK_MODAL, this.onBookmarkModal);
            EventBus.off(EventNames.REMOVE_BOOKMARK, this.onRemoveBookmark);
            EventBus.off(EventNames.UPDATE_NOTE_TAGS, this.updateUserTags);
        }

    }
</script>
<style scoped lang="scss">
    .v-dialog__content {
        max-height: 100vh;
    }

    .inner-modal-content {
        max-height: calc(90vh - 100px); /* Adjust the remaining height */
        overflow: auto;
    }
</style>
